import {
  useMutationEventOccurrenceBookRsvp,
  useMutationEventOccurrenceCancelRsvp,
  useMutationEventOccurrenceCheckInRsvp,
  useMutationEventOccurrenceLateCancelRsvp,
  useMutationEventOccurrenceNoShowRsvp,
  useMutationEventOccurrenceUncheckRsvp,
} from "@gymflow/api";
import { cn, ordinal, toLowerCaseExceptFirstChar } from "@gymflow/helpers";
import {
  EventOccurrenceDTO,
  EventRsvpDTO,
  EventRsvpStatus,
  UserMemberBean,
} from "@gymflow/types";
import { createColumnHelper } from "@tanstack/react-table";
import { isAxiosError } from "axios";
import classNames from "classnames";
import { useCallback, useContext, useMemo } from "react";

import useMemberRules from "../../../hooks/useMemberRules";
import { ModalContext } from "../../../providers";
import useGymflowModels from "../../../store";
import { Badge, Button } from "../../atoms";
import {
  CollectPastDueInvoiceSidebarProviderContext,
  Table,
} from "../../organisms";
import { ConfirmModal } from "../../templates";
import { UserMemberAvatar } from "../UserMemberAvatar";

type NonRecursiveRsvpType = {
  [rsvpKey in keyof EventRsvpDTO]: rsvpKey extends "userMember"
    ? {
        [memberKey in keyof UserMemberBean]: memberKey extends
          | "assignedStaffMembers"
          | "subscriptions"
          | "homeClub"
          | "lead"
          ? never
          : UserMemberBean[memberKey];
      }
    : EventRsvpDTO[rsvpKey];
};

export function RsvpTable({
  rsvpList = [],
  startDate,
  isLoading,
  occurrence,
}: {
  occurrence: EventOccurrenceDTO;
  rsvpList: EventRsvpDTO[];
  startDate?: string;
  isLoading: boolean;
}) {
  const sortedRsvpList = useMemo(() => {
    const statusOrder: EventRsvpStatus[] = [
      "ATTENDED",
      "BOOKED",
      "WAITING",
      "CANCELLED",
      "LATE_CANCELLED",
      "NO_SHOW",
    ];

    return [...rsvpList].sort((a, b) => {
      const aIdx = statusOrder.indexOf(a.status);
      const bIdx = statusOrder.indexOf(b.status);
      if (aIdx === -1) {
        return 1;
      }
      if (bIdx === -1) {
        return -1;
      }

      return aIdx - bIdx;
    });
  }, [rsvpList]);

  const { api } = useGymflowModels();
  const { validateMemberCancel, validateNoShow } = useMemberRules({
    activityId: occurrence.event.activity.id,
  });
  const { openCollectPastDueInvoiceSidebar } = useContext(
    CollectPastDueInvoiceSidebarProviderContext,
  );

  const { mutateAsync: mutateAsyncEventOccurrenceCancelRsvp } =
    useMutationEventOccurrenceCancelRsvp({ api });

  const { mutateAsync: mutateAsyncEventOccurrenceLateCancelRsvp } =
    useMutationEventOccurrenceLateCancelRsvp({ api });

  const { mutateAsync: mutateAsyncEventOccurrenceNoShowRsvp } =
    useMutationEventOccurrenceNoShowRsvp({ api });

  const { mutateAsync: mutateAsyncEventOccurrenceUncheckRsvp } =
    useMutationEventOccurrenceUncheckRsvp({ api });

  const { mutateAsync: mutateAsyncEventOccurrenceCheckInRsvp } =
    useMutationEventOccurrenceCheckInRsvp({ api });

  const { mutateAsync: mutateAsyncEventOccurrenceBookRsvp } =
    useMutationEventOccurrenceBookRsvp({ api });

  const { setModal, hide: hideModal } = useContext(ModalContext);

  const cancelRsvp = useCallback(
    async ({
      isLate,
      rsvpId,
      userMemberId,
    }: {
      isLate: boolean;
      rsvpId: number;
      userMemberId: string;
    }) => {
      let invoiceNumber;
      if (isLate) {
        const dataLateCancel = await mutateAsyncEventOccurrenceLateCancelRsvp({
          rsvpId,
        });
        invoiceNumber = dataLateCancel.invoiceNumber;
      } else {
        await mutateAsyncEventOccurrenceCancelRsvp({
          rsvpId,
        });
      }

      if (!invoiceNumber) {
        hideModal();
        return;
      }

      openCollectPastDueInvoiceSidebar({
        invoiceNumber,
        userMemberId,
        title: "Late Cancellation Fee Payment",
      });
      hideModal();
    },
    [
      hideModal,
      mutateAsyncEventOccurrenceCancelRsvp,
      mutateAsyncEventOccurrenceLateCancelRsvp,
      openCollectPastDueInvoiceSidebar,
    ],
  );

  const markNoShow = useCallback(
    async ({
      rsvpId,
      userMemberId,
    }: {
      rsvpId: number;
      userMemberId: string;
    }) => {
      const { invoiceNumber } = await mutateAsyncEventOccurrenceNoShowRsvp({
        rsvpId,
      });
      if (!invoiceNumber) {
        hideModal();
        return;
      }

      openCollectPastDueInvoiceSidebar({
        invoiceNumber,
        userMemberId,
        title: "No Show Fee Payment",
      });
      hideModal();
    },
    [
      hideModal,
      mutateAsyncEventOccurrenceNoShowRsvp,
      openCollectPastDueInvoiceSidebar,
    ],
  );

  const columnHelper = createColumnHelper<NonRecursiveRsvpType>();
  const columns = [
    columnHelper.accessor("userMember", {
      header: () => <div className="pl-3">Name</div>,
      cell: (cell) => {
        return (
          <div className="pl-6">
            <UserMemberAvatar {...cell.getValue()} />
          </div>
        );
      },
    }),
    columnHelper.accessor("userMember.eventsAttended", {
      header: "Class Count",
      cell: (column) => {
        const attendedCount = column.row.original.userMember.eventsAttended;
        let badgeText = "";
        let celebrationIcon = "";

        if (attendedCount === 0) {
          badgeText = "1st booking";
          celebrationIcon = "💪";
        } else if (attendedCount === 1) {
          badgeText = "1st class";
          celebrationIcon = "🥇";
        } else if (attendedCount % 25 === 0) {
          badgeText = ordinal(attendedCount) + " class";
          celebrationIcon = "🔥";
        } else {
          badgeText = ordinal(attendedCount) + " class";
        }
        return (
          <div>
            <Badge>{`${badgeText} ${celebrationIcon}`}</Badge>
          </div>
        );
      },
    }),
    columnHelper.accessor("userMember.profileType", {
      header: "User Type",
      cell: (column) => {
        return (
          <div>
            <Badge>
              {toLowerCaseExceptFirstChar(
                column.row.original.userMember.profileType,
              )}
            </Badge>
          </div>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: "Booking Status",
      cell: (column) => {
        switch (column.row.original.status) {
          case "ATTENDED":
            return (
              <div>
                <Badge intent="success">Attended</Badge>
              </div>
            );
          case "BOOKED":
            return (
              <div>
                <Badge intent="primary">Booked</Badge>
              </div>
            );
          case "CANCELLED":
            return (
              <div>
                <Badge intent="warning">Cancelled</Badge>
              </div>
            );
          case "LATE_CANCELLED":
            return (
              <div>
                <Badge intent="warning">Late Cancellation</Badge>
              </div>
            );
          case "WAITING":
            return (
              <div>
                <Badge intent="primary">Waitlist</Badge>
              </div>
            );
          case "NO_SHOW":
            return (
              <div>
                <Badge intent="error">No Show</Badge>
              </div>
            );
        }
        return <div></div>;
      },
    }),
    columnHelper.display({
      header: "Actions",
      cell: (column) => {
        const eventRow = column.row.original;
        return (
          <div className="flex gap-4">
            <Button
              intent="link"
              className={classNames({ hidden: eventRow.status !== "BOOKED" })}
              onClick={async () => {
                await mutateAsyncEventOccurrenceCheckInRsvp({
                  rsvpId: column.row.original.id,
                });
              }}
            >
              Check In
            </Button>
            <Button
              intent="link"
              className={classNames({ hidden: eventRow.status !== "ATTENDED" })}
              onClick={async () => {
                await mutateAsyncEventOccurrenceUncheckRsvp({
                  rsvpId: column.row.original.id,
                });
              }}
            >
              Un-Check
            </Button>
            <Button
              intent="link"
              className={classNames({ hidden: eventRow.status !== "BOOKED" })}
              onClick={async () => {
                const noShowMessage = validateNoShow();
                if (noShowMessage === null) {
                  await mutateAsyncEventOccurrenceNoShowRsvp({
                    rsvpId: column.row.original.id,
                  });
                } else {
                  setModal(
                    <ConfirmModal
                      onHide={() => {
                        hideModal();
                      }}
                      onCancel={async () => {
                        hideModal();
                      }}
                      onConfirm={async () => {
                        await markNoShow({
                          rsvpId: column.row.original.id,
                          userMemberId: column.row.original.userMember.id,
                        });
                        hideModal();
                      }}
                      cancelText="Cancel"
                      confirmText="Yes"
                      title="No Show Fee"
                    >
                      {noShowMessage}
                    </ConfirmModal>,
                  );
                }
              }}
            >
              No Show
            </Button>
            <Button
              intent="link"
              className={classNames({
                hidden:
                  eventRow.status !== "CANCELLED" &&
                  eventRow.status !== "WAITING" &&
                  eventRow.status !== "NO_SHOW" &&
                  eventRow.status !== "LATE_CANCELLED",
              })}
              onClick={async () => {
                try {
                  await mutateAsyncEventOccurrenceBookRsvp({
                    rsvpId: column.row.original.id,
                    ignoreCapacityCheck: false,
                  });
                } catch (e) {
                  if (isAxiosError(e)) {
                    if (
                      e.response?.data.error_code ===
                      "EVENT_OCCURRENCE_HAS_FULL_BOOKING_CAPACITY"
                    ) {
                      setModal(
                        <ConfirmModal
                          onConfirm={async () => {
                            await mutateAsyncEventOccurrenceBookRsvp({
                              rsvpId: column.row.original.id,
                              ignoreCapacityCheck: true,
                            });

                            hideModal();
                          }}
                          onCancel={() => hideModal()}
                          title="Confirm Overbooking"
                        >
                          This class is already full, please confirm you are
                          okay with overbooking this class.
                        </ConfirmModal>,
                      );
                    }
                  }
                }
              }}
            >
              Book In
            </Button>
            <Button
              intent="link"
              className={cn({ hidden: eventRow.status !== "BOOKED" })}
              onClick={async () => {
                const lateCancelMessage =
                  eventRow.status === "BOOKED"
                    ? validateMemberCancel(startDate!, true)
                    : null;
                if (lateCancelMessage === null) {
                  await mutateAsyncEventOccurrenceCancelRsvp({
                    rsvpId: column.row.original.id,
                  });
                } else {
                  setModal(
                    <ConfirmModal
                      onHide={() => {
                        hideModal();
                      }}
                      onCancel={async () => {
                        await cancelRsvp({
                          isLate: false,
                          rsvpId: column.row.original.id,
                          userMemberId: column.row.original.userMember.id,
                        });
                        hideModal();
                      }}
                      onConfirm={async () => {
                        await cancelRsvp({
                          isLate: true,
                          rsvpId: column.row.original.id,
                          userMemberId: column.row.original.userMember.id,
                        });
                        hideModal();
                      }}
                      cancelText="No"
                      confirmText="Yes"
                      title="Late Cancellation Fee"
                    >
                      {lateCancelMessage}
                    </ConfirmModal>,
                  );
                }
              }}
            >
              Cancel
            </Button>
            <Button
              intent="link"
              className={classNames({
                hidden: eventRow.status !== "WAITING",
              })}
              onClick={async () => {
                await mutateAsyncEventOccurrenceCancelRsvp({
                  rsvpId: column.row.original.id,
                });
              }}
            >
              Remove
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <Table
      rowClassName="h-14"
      data={sortedRsvpList}
      columns={columns}
      pageCount={1}
      pageIndex={0}
      isFetching={isLoading}
      pageSize={sortedRsvpList.length ?? 10}
    />
  );
}
